import React from 'react'
import { Controller, FormProvider } from 'react-hook-form'

import {
  CorporateSelect,
  DatePickerForm,
  LengthOfStay,
  PlaceAutocomplete,
  Select,
  Spinner,
  TextArea,
  ToggleAndDescription,
} from '@/components/uikit'
import { useIsUserPwCMe } from '@/hooks/useIsUserPwCMe'
import { theme } from '@/theme'

import { SuccessfulHOR } from './components'
import { Option } from './components/Option'
import { OptionalInformationFormSection } from './components/OptionalInformationFormSection'
import {
  CHECK_IN_DATE_FORMAT,
  PARKING_OPTIONS,
  BEDROOM_OPTIONS,
  PETS_FIELD_FORM_RULES,
} from './constants'
import { useAccommodation } from './hooks'
import * as S from './styles'

import { Layout, Message, SignOutButton } from '../../components'
import {
  TextInput,
  RadioInput,
  AmountSelector,
  FormItem,
  Checkbox,
  CurrencySelect,
} from '../../components/uikit'

import type { AccommodationFormData } from './types'

export const AccommodationApp = () => {
  const {
    handleAnotherRequest,
    message,
    methods,
    onCloseMessage,
    onSubmitForm,
    showSuccessfulHOR,
    canSubmit,
    currencySymbol,
    canShowImage,
    mapImage,
    officeSuggestions,
    onChangeOfficeLocationHandler,
    addressLocation,
    dictionary,
    onChangeLengthOfStay,
    onChangeDate,
  } = useAccommodation()

  const isPwCME = useIsUserPwCMe()

  let content = null
  if (!canSubmit) {
    content = (
      <S.SpinnerContainer>
        <Spinner size={32} color={theme.colors.primaryDefault} />
      </S.SpinnerContainer>
    )
  } else if (showSuccessfulHOR) {
    content = (
      <>
        <SuccessfulHOR onClickAnotherRequest={handleAnotherRequest} />
        <S.SignoutSection />
      </>
    )
  } else {
    const { errors } = methods.formState
    content = (
      <>
        <FormProvider {...methods}>
          <form onSubmit={onSubmitForm}>
            <S.FormSection>
              <S.SectionTitle>Stay Details</S.SectionTitle>
              <S.SectionContent>
                <FormItem
                  label={`${dictionary.corporate} *` || 'Corporate *'}
                  error={errors?.company}
                >
                  <Controller
                    control={methods.control}
                    rules={{ required: 'Field is required' }}
                    name="company"
                    render={({ field }) => (
                      <CorporateSelect
                        isClearable
                        requestParams={{
                          /* ...(firstFieldSet === 'rmc'
                        ? rmcFilterRequestParams
                        : {}), */
                          sort: 'name',
                          order: 'asc',
                        }}
                        {...field}
                      />
                    )}
                  />
                </FormItem>

                <TextInput
                  label={`${dictionary.fileNumberOrReference} *`}
                  placeholder={dictionary.fileNumberEmployeeIdOrReference}
                  {...methods.register('reference', {
                    required: 'Field is required',
                  })}
                  error={errors?.reference}
                />

                <PlaceAutocomplete<AccommodationFormData>
                  control={methods.control}
                  error={errors?.address?.location}
                  rules={{ required: 'Field is required' }}
                  label={'Preferred Location *'}
                  placeholder={dictionary.enterYourLocationOfPreference}
                  name="address.location"
                  disabled={!canSubmit}
                  onChange={onChangeOfficeLocationHandler}
                  hasDropdown={false}
                  isClearable
                  value={addressLocation}
                  initialInputValue={addressLocation ?? ''}
                  officeSuggestions={officeSuggestions}
                  additionalExtraComponents={{ Option }}
                />

                {canShowImage && (
                  <S.OfficeMap
                    src={URL.createObjectURL(mapImage)}
                    alt="Office Map"
                  />
                )}

                <DatePickerForm
                  isFullWidth
                  label="Check-in *"
                  dateFormat={CHECK_IN_DATE_FORMAT}
                  name="lengthOfStay.checkIn"
                  isRequired
                  rules={{ required: 'Field is required' }}
                  shouldCloseOnSelect
                  error={errors?.lengthOfStay?.checkIn}
                  monthsShown={1}
                  selected={methods.watch('lengthOfStay.checkIn')}
                  onChange={onChangeDate}
                />

                <DatePickerForm
                  isFullWidth
                  label="Check-out *"
                  dateFormat={CHECK_IN_DATE_FORMAT}
                  name="lengthOfStay.checkOut"
                  isRequired
                  rules={{ required: 'Field is required' }}
                  shouldCloseOnSelect
                  error={errors?.lengthOfStay?.checkOut}
                  monthsShown={1}
                  selected={methods.watch('lengthOfStay.checkOut')}
                  onChange={onChangeDate}
                />

                <LengthOfStay
                  daysFormKey="lengthOfStay.days"
                  nightsFormKey="lengthOfStay.nights"
                  error={
                    errors.lengthOfStay?.checkIn ||
                    errors.lengthOfStay?.checkOut
                  }
                  onChangeLengthOfStay={onChangeLengthOfStay}
                  isDisabled={!canSubmit}
                />

                <Select
                  label="Number of Bedrooms *"
                  control={methods.control}
                  name="bedrooms"
                  rules={{ required: 'Field is required' }}
                  placeholder="Select"
                  options={BEDROOM_OPTIONS}
                  error={errors?.bedrooms}
                />

                <CurrencySelect
                  label="Currency *"
                  name="currency"
                  control={methods.control}
                  error={errors?.currency}
                  rules={{ required: 'Field is required' }}
                />

                <AmountSelector
                  label="Budget per night"
                  control={methods.control}
                  allowEmpty
                  name="maxBudgetPerNight"
                  asTextInput
                  postfix={currencySymbol}
                  error={errors?.maxBudgetPerNight}
                />

                <RadioInput
                  name="parking"
                  label="Parking"
                  options={PARKING_OPTIONS}
                  control={methods.control}
                  isBulletDesign
                  shareWidth
                />

                <ToggleAndDescription
                  toggleFormField="petFriendlyRequired"
                  toggleLabel="Pet-friendly Required"
                >
                  {isPwCME && (
                    <TextArea
                      {...methods.register('pets', PETS_FIELD_FORM_RULES)}
                      error={errors.pets}
                      label="Pets Requirements"
                      placeholder="Specify quantity, type breed and weight"
                    />
                  )}
                </ToggleAndDescription>

                <FormItem label="Priority Request">
                  <Checkbox
                    name="isVipRequest"
                    label="Mark as VIP"
                    {...methods.register('isVipRequest')}
                  />
                </FormItem>
              </S.SectionContent>
            </S.FormSection>

            <S.FormSection>
              <S.SectionTitle>{dictionary.guest} Details</S.SectionTitle>
              <S.SectionContent>
                <S.TwoColumnGrid>
                  <TextInput
                    name="personalInfoFirstName"
                    label="First Name"
                    {...methods.register('personalInfoFirstName')}
                    error={errors?.personalInfoFirstName}
                  />

                  <TextInput
                    name="personalInfoLastName"
                    label="Last Name"
                    {...methods.register('personalInfoLastName')}
                    error={errors?.personalInfoLastName}
                  />
                </S.TwoColumnGrid>

                <TextInput
                  name="personalInfoEmail"
                  {...methods.register('personalInfoEmail')}
                  label={dictionary.email}
                  error={errors?.personalInfoEmail}
                />

                <S.TwoColumnGrid>
                  <AmountSelector
                    label="Adults *"
                    control={methods.control}
                    allowEmpty
                    name="numberOfAdultsGuest"
                  />
                  <AmountSelector
                    label="Children"
                    control={methods.control}
                    allowEmpty
                    name="numberOfChildrenGuest"
                  />
                </S.TwoColumnGrid>
              </S.SectionContent>
            </S.FormSection>

            <OptionalInformationFormSection />

            <S.SubmitButtonWrapper>
              <S.SubmitButton
                type="submit"
                isFullWidth
                disabled={!canSubmit}
                icon={
                  canSubmit ? (
                    <i className="fas fa-spinner fa-spin" />
                  ) : (
                    <i className="fas fa-paper-plane" />
                  )
                }
              >
                Send Request
              </S.SubmitButton>
            </S.SubmitButtonWrapper>
          </form>
        </FormProvider>
        <SignOutButton />
      </>
    )
  }
  return (
    <Layout
      title={!showSuccessfulHOR && 'Housing Request'}
      subtitle={
        !showSuccessfulHOR &&
        'Our experts will match you with handpicked, vetted stays tailored to your needs'
      }
    >
      {content}

      {message && (
        <S.MessageWrapper>
          <Message type={message.type} onClose={onCloseMessage}>
            {message.text}
          </Message>
        </S.MessageWrapper>
      )}
    </Layout>
  )
}
